import { useState, useMemo, useEffect, useRef } from "react";
import { computeFloorplanStats, filterStatementsToLenders } from "@/lib/floorplanStats";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useOpAnalytics } from "@/hooks/useOpAnalytics";
import type { DealerProfile } from "@shared/schema";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Target, TrendingUp, DollarSign, Car, Clock,
  ArrowRight, Info, AlertTriangle, CheckCircle2, XCircle,
  Pencil, Save, X, BarChart3, ChevronDown,
  Building2, Layers, Receipt, LayoutGrid,
  Calendar, ShieldAlert, ShieldCheck, Upload, Loader2, Bell, Mail,
} from "lucide-react";
import type { DataSlaType, DealerDataSla } from "@shared/schema";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isDealerProfileComplete(profile: DealerProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.onboardingCompleted) return true;
  const hasString = (v: string | null | undefined) => v != null && v.trim().length > 0;
  const hasNumber = (v: number | null | undefined) => v != null && v > 0;
  return (
    hasString(profile.dealershipName) &&
    hasString(profile.inventoryType) &&
    hasString(profile.address) &&
    hasNumber(profile.avgSalesPerMonth != null ? parseFloat(String(profile.avgSalesPerMonth)) : null) &&
    hasNumber(profile.totalHeadcount)
  );
}

function fmt$(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: decimals });
}

function fmtNum(v: number | null | undefined, decimals = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: decimals });
}

function nf(v: string | number | null | undefined): number {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function pctOf(actual: number | null, goal: number | null): number | null {
  if (actual == null || goal == null || goal === 0) return null;
  return actual / goal;
}

function statusColor(pct: number | null, higherIsBetter = true): "green" | "yellow" | "red" {
  if (pct == null) return "yellow";
  const v = higherIsBetter ? pct : pct === 0 ? 99 : 1 / pct;
  if (v >= 0.95) return "green";
  if (v >= 0.75) return "yellow";
  return "red";
}

// ─── GoalRow ──────────────────────────────────────────────────────────────────

function GoalRow({
  label, actual, goal, actualFormatted, goalFormatted, higherIsBetter = true, testId, formatGap, exceedLabel,
}: {
  label: string; actual: number | null; goal: number | null;
  actualFormatted: string; goalFormatted: string;
  higherIsBetter?: boolean; testId?: string;
  formatGap?: (gap: number) => string;
  exceedLabel?: string;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const resolvedExceedLabel = exceedLabel ?? t("pages:sla.goalRow.exceededLabel");
  const pct = pctOf(actual, goal);
  const color = statusColor(pct, higherIsBetter);
  const pctDisplay = pct != null ? Math.round(Math.min(Math.abs(pct), 1) * 100) : null;
  const hasGoal = goal != null && goal !== 0;

  const tooltipText = useMemo(() => {
    if (actual == null || goal == null || goal === 0 || formatGap == null) return null;
    const gap = Math.abs(goal - actual);
    const exceeded = higherIsBetter ? actual >= goal : actual <= goal;
    if (exceeded) return t("pages:sla.goalRow.tooltipExceeded", { action: resolvedExceedLabel, gap: formatGap(gap) });
    return t("pages:sla.goalRow.tooltipAway", { gap: formatGap(gap) });
  }, [actual, goal, higherIsBetter, formatGap, resolvedExceedLabel, t]);

  const card = (
    <div className="px-4 py-3.5 rounded-xl bg-muted/40 space-y-2 cursor-default">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {hasGoal ? (
          <div className="flex items-center gap-2">
            {color === "green" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
            {color === "yellow" && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />}
            {color === "red" && <XCircle className="w-3.5 h-3.5 text-destructive" />}
            {pctDisplay != null && (
              <Badge
                variant="secondary"
                className={`text-xs ${
                  color === "green"
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : color === "yellow"
                      ? "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
                      : "bg-destructive/10 text-destructive"
                }`}
              >
                {pctDisplay}%
              </Badge>
            )}
          </div>
        ) : (
          <Badge variant="outline" className="text-xs text-muted-foreground">{t("pages:sla.goalRow.noGoalSet")}</Badge>
        )}
      </div>
      <div className="flex items-end justify-between text-sm">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">{t("pages:sla.goalRow.actual")}</p>
          <p className="font-bold text-lg tabular-nums" data-testid={testId}>{actualFormatted}</p>
        </div>
        {hasGoal && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground mb-0.5">{t("pages:sla.goalRow.goal")}</p>
            <p className="font-semibold text-muted-foreground tabular-nums">{goalFormatted}</p>
          </div>
        )}
      </div>
      {hasGoal && pctDisplay != null && (
        <Progress
          value={pctDisplay}
          className={`h-1.5 ${
            color === "green"
              ? "[&>div]:bg-emerald-500"
              : color === "yellow"
                ? "[&>div]:bg-yellow-500"
                : "[&>div]:bg-destructive"
          }`}
        />
      )}
    </div>
  );

  if (!tooltipText) return card;

  return (
    <UITooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs text-center">
        {tooltipText}
      </TooltipContent>
    </UITooltip>
  );
}

// ─── SummaryBadges ────────────────────────────────────────────────────────────

function SummaryBadges({ green, yellow, red }: { green: number; yellow: number; red: number }) {
  const { t } = useTranslation(["pages", "common"]);
  const total = green + yellow + red;
  if (total === 0) return null;
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap">
      {green > 0 && (
        <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" />{t("pages:sla.summary.onTrack", { count: green })}
        </span>
      )}
      {yellow > 0 && (
        <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
          <AlertTriangle className="w-3.5 h-3.5" />{t("pages:sla.summary.needsAttention", { count: yellow })}
        </span>
      )}
      {red > 0 && (
        <span className="flex items-center gap-1 text-destructive">
          <XCircle className="w-3.5 h-3.5" />{t("pages:sla.summary.offTrack", { count: red })}
        </span>
      )}
    </div>
  );
}

// ─── GoalsEditor ─────────────────────────────────────────────────────────────

interface GoalFields {
  goalAvgSalesPerMonth: string;
  goalAvgGrossProfitPerUnit: string;
  goalAvgTurnDays: string;
  goalAvgMonthlyRevenue: string;
  goalMonthlyOperatingExpenses: string;
  goalAvgUnitsOnLot: string;
}

function makeGoalFields(profile: DealerProfile | null): GoalFields {
  return {
    goalAvgSalesPerMonth: profile?.goalAvgSalesPerMonth != null ? String(nf(profile.goalAvgSalesPerMonth)) : "",
    goalAvgGrossProfitPerUnit: profile?.goalAvgGrossProfitPerUnit != null ? String(nf(profile.goalAvgGrossProfitPerUnit)) : "",
    goalAvgTurnDays: profile?.goalAvgTurnDays != null ? String(nf(profile.goalAvgTurnDays)) : (profile?.avgTurnDays != null ? String(nf(profile.avgTurnDays)) : ""),
    goalAvgMonthlyRevenue: profile?.goalAvgMonthlyRevenue != null ? String(nf(profile.goalAvgMonthlyRevenue)) : "",
    goalMonthlyOperatingExpenses: profile?.goalMonthlyOperatingExpenses != null ? String(nf(profile.goalMonthlyOperatingExpenses)) : "",
    goalAvgUnitsOnLot: profile?.goalAvgUnitsOnLot != null ? String(nf(profile.goalAvgUnitsOnLot)) : "",
  };
}

function GoalsEditor({ profile, onSaved }: { profile: DealerProfile | null; onSaved: () => void }) {
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<GoalFields>(() => makeGoalFields(profile));

  // Resync when profile async data arrives
  useEffect(() => {
    if (!editing) {
      setFields(makeGoalFields(profile));
    }
  }, [profile, editing]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/dealer-profile", {
        goalAvgSalesPerMonth: nf(fields.goalAvgSalesPerMonth) || null,
        goalAvgGrossProfitPerUnit: nf(fields.goalAvgGrossProfitPerUnit) || null,
        goalAvgTurnDays: nf(fields.goalAvgTurnDays) || null,
        goalAvgMonthlyRevenue: nf(fields.goalAvgMonthlyRevenue) || null,
        goalMonthlyOperatingExpenses: nf(fields.goalMonthlyOperatingExpenses) || null,
        goalAvgUnitsOnLot: nf(fields.goalAvgUnitsOnLot) || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      toast({ title: t("pages:sla.goalsEditor.goalsSaved") });
      setEditing(false);
      onSaved();
    },
    onError: (err: Error) => {
      toast({ title: t("pages:sla.goalsEditor.failedSave"), description: err.message, variant: "destructive" });
    },
  });

  const upd = (k: keyof GoalFields, v: string) => setFields((f) => ({ ...f, [k]: v }));

  if (!editing) {
    return (
      <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="button-edit-goals" className="gap-1.5">
        <Pencil className="w-3.5 h-3.5" />{t("pages:sla.goalsEditor.editGoals")}
      </Button>
    );
  }

  const goalDefs: { label: string; key: keyof GoalFields; prefix?: string; suffix?: string }[] = [
    { label: t("pages:sla.goalsEditor.fields.goalAvgSalesPerMonth"), key: "goalAvgSalesPerMonth", suffix: t("pages:sla.common.units") },
    { label: t("pages:sla.goalsEditor.fields.goalAvgGrossProfitPerUnit"), key: "goalAvgGrossProfitPerUnit", prefix: "$" },
    { label: t("pages:sla.goalsEditor.fields.goalAvgTurnDays"), key: "goalAvgTurnDays", suffix: t("pages:sla.common.days") },
    { label: t("pages:sla.goalsEditor.fields.goalAvgMonthlyRevenue"), key: "goalAvgMonthlyRevenue", prefix: "$" },
    { label: t("pages:sla.goalsEditor.fields.goalMonthlyOperatingExpenses"), key: "goalMonthlyOperatingExpenses", prefix: "$" },
    { label: t("pages:sla.goalsEditor.fields.goalAvgUnitsOnLot"), key: "goalAvgUnitsOnLot", suffix: t("pages:sla.common.units") },
  ];

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{t("pages:sla.goalsEditor.setYourGoals")}</CardTitle>
          <Button size="icon" variant="ghost" onClick={() => setEditing(false)} className="h-7 w-7" data-testid="button-cancel-goals">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
        <CardDescription className="text-xs">{t("pages:sla.goalsEditor.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {goalDefs.map(({ label, key, prefix, suffix }) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <div className="relative">
                {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{prefix}</span>}
                <Input
                  type="text" inputMode="numeric"
                  value={fields[key]}
                  onChange={(e) => upd(key, e.target.value.replace(/[^0-9.]/g, ""))}
                  className={`${prefix ? "pl-6" : ""} ${suffix ? "pr-12" : ""} text-sm`}
                  placeholder="—"
                  data-testid={`input-goal-${key}`}
                />
                {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{suffix}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-goals" className="gap-1.5">
            <Save className="w-3.5 h-3.5" />
            {saveMutation.isPending ? t("pages:sla.common.saving") : t("pages:sla.goalsEditor.saveGoals")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="button-cancel-goals">{t("pages:sla.common.cancel")}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── CurrentProfileEditor ────────────────────────────────────────────────────

interface CurrentFields {
  dealerType: string;
  avgUnitsOnLot: string;
  avgSalesPerMonth: string;
  avgGrossProfitPerUnit: string;
  avgVehicleValue: string;
  avgMonthlyRevenue: string;
  monthlyOperatingExpenses: string;
  lotCapacity: string;
  avgTurnDays: string;
  avgReconDays: string;
}

const DEALER_TYPE_LABELS: Record<string, string> = {
  retail: "Retail",
  used_retail: "Retail",
  wholesale: "Wholesale",
  retail_wholesale: "Retail + Wholesale",
  bhph: "Buy Here Pay Here",
  franchise: "Franchise",
};

function makeCurrentFields(profile: DealerProfile | null): CurrentFields {
  return {
    dealerType: profile?.dealerType || (profile as any)?.inventoryType || "",
    avgUnitsOnLot: profile?.avgUnitsOnLot != null ? String(nf(profile.avgUnitsOnLot)) : "",
    avgSalesPerMonth: profile?.avgSalesPerMonth != null ? String(nf(profile.avgSalesPerMonth)) : "",
    avgGrossProfitPerUnit: profile?.avgGrossProfitPerUnit != null ? String(nf(profile.avgGrossProfitPerUnit)) : "",
    avgVehicleValue: profile?.avgVehicleValue != null ? String(nf(profile.avgVehicleValue)) : "",
    avgMonthlyRevenue: profile?.avgMonthlyRevenue != null ? String(nf(profile.avgMonthlyRevenue)) : "",
    monthlyOperatingExpenses: profile?.monthlyOperatingExpenses != null ? String(nf(profile.monthlyOperatingExpenses)) : "",
    lotCapacity: profile?.lotCapacity != null ? String(nf(profile.lotCapacity)) : "",
    avgTurnDays: profile?.avgTurnDays != null ? String(nf(profile.avgTurnDays)) : "",
    avgReconDays: profile?.avgReconDays != null ? String(nf(profile.avgReconDays)) : "",
  };
}

type RealValueEntry = { value: number; source: string };
type RealValuesMap = Partial<Record<Exclude<keyof CurrentFields, "dealerType">, RealValueEntry>>;

function CurrentProfileEditor({ profile, autoAvgTurnDays, realValues }: { profile: DealerProfile | null; autoAvgTurnDays?: number | null; realValues?: RealValuesMap }) {
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<CurrentFields>(() => makeCurrentFields(profile));

  // Resync when profile async data arrives
  useEffect(() => {
    if (!editing) {
      setFields(makeCurrentFields(profile));
    }
  }, [profile, editing]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/dealer-profile", {
        dealerType: (fields.dealerType as "retail" | "wholesale" | "retail_wholesale") || null,
        avgUnitsOnLot: nf(fields.avgUnitsOnLot) || null,
        avgSalesPerMonth: nf(fields.avgSalesPerMonth) || null,
        avgGrossProfitPerUnit: nf(fields.avgGrossProfitPerUnit) || null,
        avgVehicleValue: nf(fields.avgVehicleValue) || null,
        avgMonthlyRevenue: nf(fields.avgMonthlyRevenue) || null,
        monthlyOperatingExpenses: nf(fields.monthlyOperatingExpenses) || null,
        lotCapacity: nf(fields.lotCapacity) || null,
        avgTurnDays: nf(fields.avgTurnDays) || null,
        avgReconDays: nf(fields.avgReconDays) || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      toast({ title: t("pages:sla.current.profileUpdated") });
      setEditing(false);
      // Fire-and-forget: sync updated metrics back into onboarding phase snapshots
      apiRequest("POST", "/api/dealer-profile/sync-onboarding").catch((e: unknown) => {
        console.warn("[dealer-profile] onboarding sync failed (non-fatal):", e);
      });
    },
    onError: (err: Error) => {
      toast({ title: t("pages:sla.current.failedUpdate"), description: err.message, variant: "destructive" });
    },
  });

  const upd = (k: keyof CurrentFields, v: string) => setFields((f) => ({ ...f, [k]: v }));

  type StatDef = {
    label: string;
    key: Exclude<keyof CurrentFields, "dealerType">;
    prefix?: string;
    suffix?: string;
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
  };

  const statDefs: StatDef[] = [
    { label: t("pages:sla.current.stats.avgUnitsOnLot"), key: "avgUnitsOnLot", suffix: t("pages:sla.common.units"), icon: Layers, iconBg: "bg-blue-500/10 dark:bg-blue-400/10", iconColor: "text-blue-600 dark:text-blue-400" },
    { label: t("pages:sla.current.stats.avgSalesPerMonth"), key: "avgSalesPerMonth", suffix: t("pages:sla.common.units"), icon: TrendingUp, iconBg: "bg-primary/10", iconColor: "text-primary" },
    { label: t("pages:sla.current.stats.avgGrossProfitPerUnit"), key: "avgGrossProfitPerUnit", prefix: "$", icon: DollarSign, iconBg: "bg-emerald-500/10 dark:bg-emerald-400/10", iconColor: "text-emerald-600 dark:text-emerald-400" },
    { label: t("pages:sla.current.stats.avgVehicleValue"), key: "avgVehicleValue", prefix: "$", icon: Car, iconBg: "bg-violet-500/10 dark:bg-violet-400/10", iconColor: "text-violet-600 dark:text-violet-400" },
    { label: t("pages:sla.current.stats.avgMonthlyRevenue"), key: "avgMonthlyRevenue", prefix: "$", icon: BarChart3, iconBg: "bg-emerald-500/10 dark:bg-emerald-400/10", iconColor: "text-emerald-600 dark:text-emerald-400" },
    { label: t("pages:sla.current.stats.monthlyOperatingExpenses"), key: "monthlyOperatingExpenses", prefix: "$", icon: Receipt, iconBg: "bg-orange-500/10 dark:bg-orange-400/10", iconColor: "text-orange-600 dark:text-orange-400" },
    { label: t("pages:sla.current.stats.lotCapacity"), key: "lotCapacity", suffix: t("pages:sla.common.units"), icon: LayoutGrid, iconBg: "bg-blue-500/10 dark:bg-blue-400/10", iconColor: "text-blue-600 dark:text-blue-400" },
    { label: t("pages:sla.current.stats.avgTurnDays"), key: "avgTurnDays", suffix: t("pages:sla.common.days"), icon: Clock, iconBg: "bg-amber-500/10 dark:bg-amber-400/10", iconColor: "text-amber-600 dark:text-amber-400" },
    { label: t("pages:sla.current.stats.avgReconDays"), key: "avgReconDays", suffix: t("pages:sla.common.days"), icon: Clock, iconBg: "bg-rose-500/10 dark:bg-rose-400/10", iconColor: "text-rose-600 dark:text-rose-400" },
  ];

  const numDefs: { label: string; key: Exclude<keyof CurrentFields, "dealerType">; prefix?: string; suffix?: string }[] = statDefs;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">{t("pages:sla.current.dealershipMetrics")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("pages:sla.current.baseline")}</p>
        </div>
        {!editing ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="button-edit-current-profile" className="gap-1.5">
            <Pencil className="w-3.5 h-3.5" />{t("pages:sla.common.edit")}
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-current-profile" className="gap-1.5">
              <Save className="w-3.5 h-3.5" />
              {saveMutation.isPending ? t("pages:sla.common.saving") : t("pages:sla.common.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="button-cancel-profile">{t("pages:sla.common.cancel")}</Button>
          </div>
        )}
      </div>

      {/* ── View mode: stat card grid ── */}
      {!editing && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Dealer Type card */}
          <div className="relative rounded-xl border bg-card p-4 flex flex-col gap-2 hover:border-primary/30 hover:shadow-sm transition-all col-span-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-violet-500/10 dark:bg-violet-400/10`}>
              <Building2 className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground leading-tight">{t("pages:sla.current.dealerType")}</p>
              <p className="text-sm font-bold mt-0.5 leading-tight" data-testid="value-current-dealerType">
                {fields.dealerType ? (t(`pages:sla.current.dealerTypeLabels.${fields.dealerType}`, { defaultValue: DEALER_TYPE_LABELS[fields.dealerType] ?? fields.dealerType })) : t("pages:sla.common.dash")}
              </p>
            </div>
          </div>

          {statDefs.map(({ label, key, prefix, suffix, icon: Icon, iconBg, iconColor }) => {
            const val = fields[key];
            const display = val
              ? prefix ? fmt$(nf(val)) : `${fmtNum(nf(val), 0)}${suffix ? ` ${suffix}` : ""}`
              : "—";
            const isEmpty = !val;
            const realEntry = realValues?.[key];
            const realDisplay = realEntry != null
              ? prefix
                ? fmt$(realEntry.value)
                : `${fmtNum(realEntry.value, 0)}${suffix ? ` ${suffix}` : ""}`
              : null;
            return (
              <div
                key={key}
                className={`relative rounded-xl border bg-card p-4 flex flex-col gap-2 hover:border-primary/30 hover:shadow-sm transition-all ${isEmpty ? "opacity-50" : ""}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
                  <p className="text-sm font-bold mt-0.5 tabular-nums leading-tight" data-testid={`value-current-${key}`}>{display}</p>
                  {realEntry != null && realDisplay != null && (
                    <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5 tabular-nums" data-testid={`real-value-${key}`}>
                      {realEntry.source}: {realDisplay}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit mode: compact input grid ── */}
      {editing && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4 rounded-xl border bg-muted/20">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("pages:sla.current.dealerType")}</Label>
            <Select value={fields.dealerType} onValueChange={(v) => upd("dealerType", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="select-dealer-type">
                <SelectValue placeholder={t("pages:sla.current.selectType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="used_retail">{t("pages:sla.current.dealerTypeLabels.used_retail")}</SelectItem>
                <SelectItem value="wholesale">{t("pages:sla.current.dealerTypeLabels.wholesale")}</SelectItem>
                <SelectItem value="retail_wholesale">{t("pages:sla.current.dealerTypeLabels.retail_wholesale")}</SelectItem>
                <SelectItem value="bhph">{t("pages:sla.current.dealerTypeLabels.bhph")}</SelectItem>
                <SelectItem value="franchise">{t("pages:sla.current.dealerTypeLabels.franchise")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {numDefs.map(({ label, key, prefix, suffix }) => {
            const val = fields[key];
            return (
              <div key={key} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <div className="relative">
                  {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{prefix}</span>}
                  <Input
                    type="text" inputMode="numeric"
                    value={val ? val.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : val}
                    onChange={(e) => upd(key, e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`${prefix ? "pl-6" : ""} ${suffix ? "pr-12" : ""} text-sm h-9`}
                    placeholder="—"
                    data-testid={`input-current-${key}`}
                  />
                  {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{suffix}</span>}
                </div>
                {key === "avgTurnDays" && autoAvgTurnDays != null && (
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {t("pages:sla.current.turnTimeReceivablesPrefix")}<span className="font-medium">{t("pages:sla.current.turnTimeReceivablesValue", { days: Math.round(autoAvgTurnDays) })}</span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Scenario typed object ────────────────────────────────────────────────────

type Dataset = "current" | "goal";

interface Scenario {
  label: string;
  gpu: number | null;
  avgVehicleValue: number | null;
  monthlyOpEx: number | null;
  monthlyUnits: number | null;
  turnDays: number | null;
}

// ─── Profit Simulator ─────────────────────────────────────────────────────────

type Period = "monthly" | "annual";

function ProfitSimulator({ current, goal }: { current: Scenario; goal: Scenario }) {
  const { t } = useTranslation(["pages", "common"]);
  const statedUnits = current.monthlyUnits ?? goal.monthlyUnits ?? null;
  const initialUnits = statedUnits != null && statedUnits > 0 ? Math.round(statedUnits) : 20;
  const [units, setUnits] = useState(initialUnits);
  const [unitsInput, setUnitsInput] = useState(String(initialUnits));
  const [unitsTouched, setUnitsTouched] = useState(false);

  // Scenario data loads async — sync the slider to the dealer's stated
  // units/month once it arrives, unless the user has already changed it.
  useEffect(() => {
    if (!unitsTouched && statedUnits != null && statedUnits > 0) {
      const v = Math.round(statedUnits);
      setUnits(v);
      setUnitsInput(String(v));
    }
  }, [statedUnits, unitsTouched]);
  const [period, setPeriod] = useState<Period>("monthly");
  const [opExOverrideInput, setOpExOverrideInput] = useState("");

  const sliderMax = Math.max(200, Math.ceil(units * 1.5 / 10) * 10);

  const hasCurrent = (current.gpu ?? 0) > 0;
  const hasGoal = (goal.gpu ?? 0) > 0;

  const curGPU = current.gpu ?? 0;
  const glGPU = goal.gpu ?? 0;

  const m = period === "annual" ? 12 : 1;
  const periodLabel = period === "monthly" ? t("pages:sla.simulator.perMo") : t("pages:sla.simulator.perYr");

  const opExOverrideMonthly: number | null = (() => {
    const raw = nf(opExOverrideInput);
    if (!raw) return null;
    return period === "annual" ? raw / 12 : raw;
  })();

  const curMonthlyOpEx = opExOverrideMonthly ?? (current.monthlyOpEx ?? 0);
  const glMonthlyOpEx = opExOverrideMonthly ?? (goal.monthlyOpEx ?? 0);

  const curGross = units * curGPU * m;
  const curOpEx = curMonthlyOpEx * m;
  const curNet = curGross - curOpEx;

  const glGross = units * glGPU * m;
  const glOpEx = glMonthlyOpEx * m;
  const glNet = glGross - glOpEx;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          {t("pages:sla.simulator.title")}
        </CardTitle>
        <CardDescription className="text-xs">
          {t("pages:sla.simulator.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Period toggle */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-muted-foreground">{t("pages:sla.simulator.periodMode")}</span>
          <div className="flex rounded-lg border border-border overflow-hidden text-xs">
            <button
              onClick={() => { setPeriod("monthly"); setOpExOverrideInput(""); }}
              className={`px-3 py-1.5 font-medium transition-colors ${period === "monthly" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="toggle-opex-monthly"
            >{t("pages:sla.simulator.monthly")}</button>
            <button
              onClick={() => { setPeriod("annual"); setOpExOverrideInput(""); }}
              className={`px-3 py-1.5 font-medium transition-colors ${period === "annual" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="toggle-opex-annual"
            >{t("pages:sla.simulator.annual")}</button>
          </div>
          <span className="text-xs text-muted-foreground">{period === "monthly" ? t("pages:sla.simulator.figuresPerMonth") : t("pages:sla.simulator.figuresPerYear")}</span>
        </div>

        {/* OpEx override */}
        <div className="flex items-center gap-3">
          <Label className="text-xs font-medium shrink-0 w-48">
            {period === "monthly" ? t("pages:sla.simulator.opexOverrideMonthly") : t("pages:sla.simulator.opexOverrideAnnual")}
          </Label>
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
            <Input
              type="text"
              inputMode="numeric"
              placeholder={period === "monthly"
                ? (current.monthlyOpEx ?? goal.monthlyOpEx) != null
                  ? t("pages:sla.simulator.placeholderExample", { value: Math.round((current.monthlyOpEx ?? goal.monthlyOpEx ?? 0)).toLocaleString() })
                  : t("pages:sla.simulator.placeholderMonthlyDefault")
                : (current.monthlyOpEx ?? goal.monthlyOpEx) != null
                  ? t("pages:sla.simulator.placeholderExample", { value: Math.round((current.monthlyOpEx ?? goal.monthlyOpEx ?? 0) * 12).toLocaleString() })
                  : t("pages:sla.simulator.placeholderAnnualDefault")
              }
              value={opExOverrideInput}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, "");
                setOpExOverrideInput(digits ? parseInt(digits, 10).toLocaleString("en-US") : "");
              }}
              className="pl-6 text-sm h-8"
              data-testid="input-opex-override"
            />
          </div>
          {opExOverrideInput && (
            <button onClick={() => setOpExOverrideInput("")} className="text-xs text-muted-foreground hover:text-foreground" data-testid="button-clear-opex-override">{t("pages:sla.simulator.clear")}</button>
          )}
        </div>

        {/* Units slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">{t("pages:sla.simulator.unitsSoldMonth")}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={unitsInput}
                onChange={(e) => {
                  setUnitsTouched(true);
                  setUnitsInput(e.target.value);
                  const v = parseInt(e.target.value, 10);
                  if (Number.isFinite(v) && v >= 0) setUnits(v);
                }}
                className="w-20 h-7 text-xs text-center"
                min={0}
                data-testid="input-sim-units"
              />
              <span className="text-xs text-muted-foreground">{t("pages:sla.simulator.units")}</span>
            </div>
          </div>
          <Slider
            min={0} max={sliderMax} step={1}
            value={[Math.min(units, sliderMax)]}
            onValueChange={([v]) => { setUnitsTouched(true); setUnits(v); setUnitsInput(String(v)); }}
            className="w-full"
            data-testid="slider-sim-units"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span>{Math.round(sliderMax / 2)}</span>
            <span>{sliderMax}</span>
          </div>
        </div>

        {(!hasCurrent && !hasGoal) && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{t("pages:sla.simulator.setGpuPrefix")}<Link href="/operations" className="underline">{t("pages:sla.simulator.operationalAnalysis")}</Link>{t("pages:sla.simulator.setGpuSuffix")}</span>
          </div>
        )}

        {/* Side-by-side results */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Current */}
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("pages:sla.simulator.current")}</p>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs text-center">
                  {t("pages:sla.simulator.projectionCurrent")}
                </TooltipContent>
              </UITooltip>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t("pages:sla.simulator.grossProfit")}{periodLabel}
                {hasCurrent && <span className="text-xs text-muted-foreground/70 ml-1">{t("pages:sla.simulator.perUnit", { value: fmt$(curGPU) })}</span>}
              </span>
              <span className="font-bold tabular-nums" data-testid="sim-current-monthly-gross">{hasCurrent ? fmt$(curGross) : t("pages:sla.common.dash")}</span>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between text-sm font-bold">
              <span>{t("pages:sla.simulator.netProfit")}{periodLabel}</span>
              <span className={`tabular-nums ${curNet >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`} data-testid="sim-current-monthly-net">{hasCurrent ? fmt$(curNet) : t("pages:sla.common.dash")}</span>
            </div>
          </div>

          {/* Goal */}
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-semibold text-primary uppercase tracking-wide">{t("pages:sla.simulator.goal")}</p>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs text-center">
                  {t("pages:sla.simulator.projectionGoal")}
                </TooltipContent>
              </UITooltip>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t("pages:sla.simulator.grossProfit")}{periodLabel}
                {hasGoal && <span className="text-xs text-muted-foreground/70 ml-1">{t("pages:sla.simulator.perUnit", { value: fmt$(glGPU) })}</span>}
              </span>
              <span className="font-bold tabular-nums" data-testid="sim-goal-monthly-gross">{hasGoal ? fmt$(glGross) : t("pages:sla.common.dash")}</span>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between text-sm font-bold">
              <span>{t("pages:sla.simulator.netProfit")}{periodLabel}</span>
              <span className={`tabular-nums ${glNet >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`} data-testid="sim-goal-monthly-net">{hasGoal ? fmt$(glNet) : t("pages:sla.common.dash")}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── GP/Unit Chart ────────────────────────────────────────────────────────────

function GPUChart({ currentGPU, goalGPU }: { currentGPU: number | null; goalGPU: number | null }) {
  const { t } = useTranslation(["pages", "common"]);
  if ((currentGPU ?? 0) === 0 && (goalGPU ?? 0) === 0) return null;
  const data = [{ label: "GP / Unit", Current: currentGPU ?? 0, Goal: goalGPU ?? 0 }];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-primary" />
          {t("pages:sla.gpuChart.title")}
        </CardTitle>
        <CardDescription className="text-xs">{t("pages:sla.gpuChart.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} margin={{ top: 5, right: 16, left: 8, bottom: 5 }} barCategoryGap="40%">
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number, name: string) => [fmt$(v), name]} contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar dataKey="Current" fill="hsl(221,83%,53%)" radius={[4, 4, 0, 0]} maxBarSize={80} />
            <Bar dataKey="Goal" fill="hsl(142,71%,45%)" radius={[4, 4, 0, 0]} maxBarSize={80} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ─── Units Sold Racetrack ─────────────────────────────────────────────────────

function UnitsSoldChart({
  currentUnits, goalUnits, breakEvenUnits,
}: {
  currentUnits: number | null;
  goalUnits: number | null;
  breakEvenUnits: number | null;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const goal = Math.round(goalUnits ?? 0);
  const current = Math.round(currentUnits ?? 0);
  if (goal === 0 && current === 0) return null;

  const trackMax = Math.max(goal, current, 1);
  const soldPct = Math.min((current / trackMax) * 100, 100);
  const bePct = breakEvenUnits != null ? Math.min((breakEvenUnits / trackMax) * 100, 100) : null;
  const goalPct = goal > 0 ? Math.min((goal / trackMax) * 100, 100) : null;

  const remaining = Math.max(0, goal - current);
  const toBreakEven = breakEvenUnits != null ? Math.max(0, breakEvenUnits - current) : null;
  const pastGoal = goal > 0 && current >= goal;
  const pastBE = breakEvenUnits == null || current >= breakEvenUnits;

  // Zone color: red → amber → green
  const barColor = pastGoal
    ? "bg-emerald-500"
    : pastBE
      ? "bg-blue-500"
      : "bg-amber-500";

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Car className="w-4 h-4 text-primary" />
          {t("pages:sla.unitsChart.title")}
        </CardTitle>
        <CardDescription className="text-xs">
          {pastGoal
            ? t("pages:sla.unitsChart.goalReached")
            : goal > 0
              ? t("pages:sla.unitsChart.toGo", { count: remaining })
              : t("pages:sla.unitsChart.setGoal")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 pt-2">

        {/* Big number */}
        <div className="flex items-end gap-3">
          <span className={`text-5xl font-black tabular-nums leading-none ${pastGoal ? "text-emerald-500" : pastBE ? "text-blue-500" : "text-amber-500"}`}>
            {fmtNum(current, 0)}
          </span>
          <div className="pb-1 space-y-0.5">
            <p className="text-xs text-muted-foreground leading-tight">{t("pages:sla.unitsChart.unitsSold")}</p>
            {goal > 0 && (
              <p className="text-xs text-muted-foreground leading-tight">
                {t("pages:sla.unitsChart.ofGoalPrefix")} <strong className="text-foreground">{fmtNum(goal, 0)}</strong> {t("pages:sla.unitsChart.ofGoalSuffix")}
              </p>
            )}
          </div>
          {pastGoal && <span className="text-2xl pb-1">🏆</span>}
        </div>

        {/* Racetrack */}
        <div className="space-y-1">
          {/* Milestone labels above track */}
          <div className="relative h-5">
            {bePct != null && (
              <div
                className="absolute flex flex-col items-center"
                style={{ left: `${bePct}%`, transform: "translateX(-50%)" }}
              >
                <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                  {t("pages:sla.unitsChart.beMilestone", { value: fmtNum(breakEvenUnits!, 0) })}
                </span>
              </div>
            )}
            {goalPct != null && goalPct < 99 && (
              <div
                className="absolute flex flex-col items-center"
                style={{ left: `${goalPct}%`, transform: "translateX(-50%)" }}
              >
                <span className="text-[10px] font-semibold text-primary whitespace-nowrap">
                  {t("pages:sla.unitsChart.goalMilestone", { value: fmtNum(goal, 0) })}
                </span>
              </div>
            )}
          </div>

          {/* Track */}
          <div className="relative h-8 rounded-full bg-muted overflow-visible">
            {/* Filled bar */}
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 ${barColor}`}
              style={{ width: `${Math.max(soldPct, current > 0 ? 4 : 0)}%` }}
            />

            {/* Break-even milestone line */}
            {bePct != null && (
              <div
                className="absolute top-0 h-full w-0.5 bg-amber-400 z-10"
                style={{ left: `${bePct}%` }}
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-amber-400 text-xs leading-none">⚑</div>
              </div>
            )}

            {/* Goal milestone line */}
            {goalPct != null && (
              <div
                className="absolute top-0 h-full w-0.5 bg-primary z-10"
                style={{ left: `${goalPct}%` }}
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-primary text-xs leading-none">🏁</div>
              </div>
            )}

            {/* Car emoji at current position — flipped to face the finish line */}
            {current > 0 && (
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-xl z-20 transition-all duration-700 select-none"
                style={{ left: `${Math.max(soldPct, 4)}%`, transform: "translateX(-50%) translateY(-50%) scaleX(-1)" }}
                title={t("pages:sla.unitsChart.unitsSoldTitle", { count: current })}
              >
                🚗
              </div>
            )}
          </div>

          {/* Scale labels below */}
          <div className="flex justify-between text-[10px] text-muted-foreground px-0.5 pt-0.5">
            <span>0</span>
            {goal > 0 && <span>{fmtNum(goal, 0)}</span>}
            {current > goal && <span className="text-emerald-500 font-semibold">{t("pages:sla.unitsChart.scaleUp", { value: fmtNum(current, 0) })}</span>}
          </div>
        </div>

        {/* Status message */}
        {goal > 0 && !pastGoal && (
          <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
            toBreakEven != null && toBreakEven > 0
              ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20"
              : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20"
          }`}>
            {toBreakEven != null && toBreakEven > 0 ? (
              toBreakEven > remaining ? (
                <><AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {t("pages:sla.unitsChart.sellMoreHitGoal", { count: remaining })}</>
              ) : (
                // Normal case — break-even comes before the goal
                <><AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {t("pages:sla.unitsChart.sellMoreBreakEven", { be: toBreakEven, rest: remaining - toBreakEven })}</>
              )
            ) : (
              <><CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                {t("pages:sla.unitsChart.moreToHitGoal", { count: remaining })}</>
            )}
          </div>
        )}
        {pastGoal && (
          <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            {t("pages:sla.unitsChart.goalSmashed")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Break-Even Card ──────────────────────────────────────────────────────────

function BreakEvenCard({
  current,
}: {
  current: Scenario;
  goal?: Scenario;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const gpu = current.gpu ?? 0;
  const monthlyOpEx = current.monthlyOpEx ?? 0;

  const breakEvenUnits = gpu > 0 && monthlyOpEx > 0 ? Math.ceil(monthlyOpEx / gpu) : null;
  const breakEvenRevenue = breakEvenUnits != null ? breakEvenUnits * (current.avgVehicleValue ?? 0) : null;

  const [aboveBeInput, setAboveBeInput] = useState("1");
  const aboveBe = Math.max(0, parseInt(aboveBeInput, 10) || 0);
  const aboveBeProfit = gpu > 0 && aboveBe > 0 ? aboveBe * gpu : null;

  const hasCurrentData = (current.gpu ?? 0) > 0 || (current.monthlyOpEx ?? 0) > 0;

  if (!hasCurrentData) return null;

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader className="pb-3">
        <div className="space-y-1">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            {t("pages:sla.breakEven.title")}
          </CardTitle>
          <CardDescription className="text-xs">
            {t("pages:sla.breakEven.description")}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t("pages:sla.breakEven.breakEvenUnits")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="breakeven-units">
              {breakEvenUnits != null ? fmtNum(breakEvenUnits, 0) : t("pages:sla.common.dash")}
            </p>
            <p className="text-xs text-muted-foreground">{t("pages:sla.breakEven.unitsPerMonth")}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t("pages:sla.breakEven.monthlyOpEx")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="breakeven-monthly-opex">
              {monthlyOpEx > 0 ? fmt$(monthlyOpEx) : t("pages:sla.common.dash")}
            </p>
            <p className="text-xs text-muted-foreground">{t("pages:sla.breakEven.totalExpenses")}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t("pages:sla.breakEven.beRevenue")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="breakeven-revenue">
              {breakEvenRevenue != null && breakEvenRevenue > 0 ? fmt$(breakEvenRevenue) : t("pages:sla.common.dash")}
            </p>
            <p className="text-xs text-muted-foreground">{t("pages:sla.breakEven.grossRevenueAtBe")}</p>
          </div>
        </div>

        {/* Above break-even profit calculator */}
        {breakEvenUnits != null && gpu > 0 && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" />
              {t("pages:sla.breakEven.aboveCalculator")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("pages:sla.breakEven.aboveDescription")}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap text-muted-foreground">{t("pages:sla.breakEven.unitsAboveBe")}</Label>
                <div className="flex items-center border border-border rounded-lg overflow-hidden bg-background">
                  <button
                    onClick={() => setAboveBeInput(String(Math.max(1, aboveBe - 1)))}
                    className="px-2.5 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted text-sm font-bold transition-colors"
                    data-testid="button-be-minus"
                  >−</button>
                  <input
                    type="number" min="1" max="200"
                    value={aboveBeInput}
                    onChange={(e) => setAboveBeInput(e.target.value)}
                    className="w-12 text-center text-sm font-bold bg-transparent border-x border-border py-1.5 outline-none tabular-nums"
                    data-testid="input-above-breakeven"
                  />
                  <button
                    onClick={() => setAboveBeInput(String(aboveBe + 1))}
                    className="px-2.5 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted text-sm font-bold transition-colors"
                    data-testid="button-be-plus"
                  >+</button>
                </div>
              </div>
              {aboveBeProfit != null && (
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">{t("pages:sla.breakEven.estMonthlyProfit")}</span>
                    <span className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400" data-testid="text-above-be-profit">
                      {fmt$(aboveBeProfit)}
                    </span>
                  </div>
                  <div className="flex flex-col ml-2">
                    <span className="text-xs text-muted-foreground">{t("pages:sla.breakEven.annualized")}</span>
                    <span className="text-sm font-semibold tabular-nums text-emerald-500/80" data-testid="text-above-be-annual">
                      {t("pages:sla.breakEven.perYr", { value: fmt$(aboveBeProfit * 12) })}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {aboveBe > 0 && gpu > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("pages:sla.breakEven.summaryPrefix")} <strong className="text-foreground">{t("pages:sla.breakEven.summaryPerUnit", { value: fmt$(gpu) })}</strong> {t("pages:sla.breakEven.summaryMid")} <strong className="text-foreground">{aboveBe}</strong> {t("pages:sla.breakEven.summaryExtraUnits", { count: aboveBe })} {t("pages:sla.breakEven.summaryMid2")} <strong className="text-emerald-600 dark:text-emerald-400">{fmt$(aboveBeProfit ?? 0)}</strong> {t("pages:sla.breakEven.summarySuffix")}
              </p>
            )}
          </div>
        )}

        {gpu === 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            {t("pages:sla.breakEven.setGpuHint")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Goal Calculator ─────────────────────────────────────────────────────────

function GrossGoalCalculator({
  dataset, current, goal, currentAnnualTotalExpenses, autoAvgTurnDays,
}: {
  dataset: Dataset;
  current: Scenario;
  goal: Scenario;
  currentAnnualTotalExpenses: number | null;
  autoAvgTurnDays: number | null;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const active = dataset === "current" ? current : goal;
  const gpuForCalc = active.gpu;
  const fallbackTurnDays = autoAvgTurnDays ?? 45;
  const turnDaysForCalc = (active.turnDays ?? 0) > 0 ? active.turnDays! : fallbackTurnDays;

  const [goalType, setGoalType] = useState<"gross" | "net">("gross");
  const [period, setPeriod] = useState<"annual" | "monthly">("annual");
  const [goalInput, setGoalInput] = useState("");
  const [turnDaysInput, setTurnDaysInput] = useState(String(Math.round(turnDaysForCalc)));
  const [carsPerPersonPerMonth, setCarsPerPersonPerMonth] = useState("13");

  const effectiveTurnDays = nf(turnDaysInput) > 0 ? nf(turnDaysInput) : turnDaysForCalc;
  const effectiveCarsPerPerson = nf(carsPerPersonPerMonth) > 0 ? nf(carsPerPersonPerMonth) : 13;
  const effectiveGPU = gpuForCalc ?? 0;

  const annualGrossGoal = useMemo(() => {
    const raw = nf(goalInput);
    if (!raw) return null;
    const annualRaw = period === "monthly" ? raw * 12 : raw;
    if (goalType === "gross") return annualRaw;
    const expenses = currentAnnualTotalExpenses ?? 0;
    return annualRaw + expenses;
  }, [goalInput, period, goalType, currentAnnualTotalExpenses]);

  const canCalculate = effectiveGPU > 0;
  const unitsPerYear = annualGrossGoal != null && effectiveGPU > 0 ? Math.ceil(annualGrossGoal / effectiveGPU) : null;
  const unitsPerMonth = unitsPerYear != null ? Math.ceil(unitsPerYear / 12) : null;
  const unitsToStock = unitsPerYear != null && effectiveTurnDays > 0 ? Math.ceil((unitsPerYear * effectiveTurnDays) / 365) : null;
  const salespeopleNeeded = unitsPerMonth != null ? Math.ceil(unitsPerMonth / effectiveCarsPerPerson) : null;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Target className="w-4 h-4 text-primary" />
          <CardTitle className="text-base">{t("pages:sla.goalCalc.title")}</CardTitle>
          <Badge variant="secondary" className="text-xs">
            {dataset === "current" ? t("pages:sla.goalCalc.badgeCurrent", { value: fmt$(gpuForCalc ?? 0) }) : t("pages:sla.goalCalc.badgeGoal", { value: fmt$(gpuForCalc ?? 0) })}
          </Badge>
        </div>
        <CardDescription>{t("pages:sla.goalCalc.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs font-medium">{t("pages:sla.goalCalc.profitObjective")}</Label>
            <div className="flex gap-2 mb-2 flex-wrap">
              <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                <button onClick={() => setGoalType("gross")} className={`px-3 py-1.5 font-medium transition-colors ${goalType === "gross" ? "bg-green-600 dark:bg-green-500 text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`} data-testid="toggle-goal-gross">{t("pages:sla.goalCalc.grossProfit")}</button>
                <button onClick={() => setGoalType("net")} className={`px-3 py-1.5 font-medium transition-colors ${goalType === "net" ? "bg-green-600 dark:bg-green-500 text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`} data-testid="toggle-goal-net">{t("pages:sla.goalCalc.netProfit")}</button>
              </div>
              <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                <button onClick={() => setPeriod("annual")} className={`px-3 py-1.5 font-medium transition-colors ${period === "annual" ? "bg-green-600 dark:bg-green-500 text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`} data-testid="toggle-period-annual">{t("pages:sla.goalCalc.annual")}</button>
                <button onClick={() => setPeriod("monthly")} className={`px-3 py-1.5 font-medium transition-colors ${period === "monthly" ? "bg-green-600 dark:bg-green-500 text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`} data-testid="toggle-period-monthly">{t("pages:sla.goalCalc.monthly")}</button>
              </div>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="text" inputMode="numeric"
                placeholder={period === "annual" ? t("pages:sla.goalCalc.placeholderAnnual") : t("pages:sla.goalCalc.placeholderMonthly")}
                value={goalInput}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  setGoalInput(digits === "" ? "" : parseInt(digits, 10).toLocaleString("en-US"));
                }}
                className="pl-7"
                data-testid="input-profit-goal"
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{t("pages:sla.goalCalc.avgTurnTime")}</Label>
              <Input type="number" placeholder={String(Math.round(turnDaysForCalc))} value={turnDaysInput} onChange={(e) => setTurnDaysInput(e.target.value)} data-testid="input-turn-days" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{t("pages:sla.goalCalc.carsPerPerson")}</Label>
              <Input type="number" placeholder="13" value={carsPerPersonPerMonth} onChange={(e) => setCarsPerPersonPerMonth(e.target.value)} data-testid="input-cars-per-person" />
            </div>
          </div>
        </div>

        {!canCalculate && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{t("pages:sla.goalCalc.gppuNeededPrefix", { dataset: dataset === "current" ? t("pages:sla.goalCalc.datasetCurrent") : t("pages:sla.goalCalc.datasetGoal") })}<Link href="/operations" className="underline">{t("pages:sla.goalCalc.operationalAnalysis")}</Link>{t("pages:sla.goalCalc.gppuNeededSuffix")}</span>
          </div>
        )}

        {annualGrossGoal != null && canCalculate && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { label: t("pages:sla.goalCalc.unitsToSellYear"), value: unitsPerYear != null ? fmtNum(unitsPerYear, 0) : t("pages:sla.common.dash"), testId: "result-units-per-year", highlight: false },
                { label: t("pages:sla.goalCalc.unitsToSellMonth"), value: unitsPerMonth != null ? fmtNum(unitsPerMonth, 0) : t("pages:sla.common.dash"), testId: "result-units-per-month", highlight: false },
                { label: t("pages:sla.goalCalc.unitsToStock"), value: unitsToStock != null ? fmtNum(unitsToStock, 0) : t("pages:sla.common.dash"), testId: "result-units-to-stock", highlight: true },
                { label: t("pages:sla.goalCalc.salespeopleNeeded"), value: salespeopleNeeded != null ? fmtNum(salespeopleNeeded, 0) : t("pages:sla.common.dash"), testId: "result-salespeople", highlight: true },
              ].map(({ label, value, testId, highlight }) => (
                <div key={label} className={`flex items-center justify-between px-4 py-3 rounded-xl ${highlight ? "bg-primary/8 border border-primary/20" : "bg-muted/40"}`}>
                  <span className="text-sm text-muted-foreground">{label}</span>
                  <p className={`text-base font-bold tabular-nums ${highlight ? "text-primary" : ""}`} data-testid={testId}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {annualGrossGoal == null && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-muted/30 text-sm text-muted-foreground">
            <Target className="w-4 h-4 shrink-0 opacity-50" />
            {t("pages:sla.goalCalc.enterObjective")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── TurnTimePlannerCard ──────────────────────────────────────────────────────

function TurnTimePlannerCard({
  autoAvgTurnDays,
  profileAvgTurnDays,
  goalAvgTurnDays,
  avgUnitsOnLot,
  avgSalesPerMonth,
  goalAvgSalesPerMonth,
  gpuForImpact,
  avgVehicleValue,
}: {
  autoAvgTurnDays: number | null;
  profileAvgTurnDays: number | null;
  goalAvgTurnDays: number | null;
  avgUnitsOnLot: number | null;
  avgSalesPerMonth: number | null;
  goalAvgSalesPerMonth: number | null;
  gpuForImpact: number | null;
  avgVehicleValue: number | null;
}) {
  const { t } = useTranslation(["pages", "common"]);
  // Mode A — stock → sales
  const [unitsInStock, setUnitsInStock] = useState("50");
  const [modeATurnDays, setModeATurnDays] = useState("45");

  // Mode B — sales → stock (goal takes priority for target; current sales as fallback)
  const [targetUnitsPerMonth, setTargetUnitsPerMonth] = useState("20");
  const [modeBTurnDays, setModeBTurnDays] = useState("45");

  // Turn Time Impact collapsible
  const [showImpact, setShowImpact] = useState(false);
  const [impactStock, setImpactStock] = useState("50");
  const [impactCurrentTurn, setImpactCurrentTurn] = useState("45");
  const [impactGoalTurn, setImpactGoalTurn] = useState("30");

  // Track which fields the user has manually edited — those are never overwritten by async hydration
  const userEdited = useRef<Set<string>>(new Set());
  const hasInitialized = useRef(false);

  // Apply real values once after async data loads, skipping any field the user already touched
  useEffect(() => {
    if (hasInitialized.current) return;
    const hasData = avgUnitsOnLot != null || profileAvgTurnDays != null || autoAvgTurnDays != null || avgSalesPerMonth != null || goalAvgSalesPerMonth != null || goalAvgTurnDays != null;
    if (!hasData) return;
    hasInitialized.current = true;

    const stock = avgUnitsOnLot != null ? String(Math.round(avgUnitsOnLot)) : "50";
    // Prefer the profile's stated avg turn days (from SLA current inputs), fall back to auto-computed
    const turn = profileAvgTurnDays != null
      ? String(Math.round(profileAvgTurnDays))
      : autoAvgTurnDays != null ? String(Math.round(autoAvgTurnDays)) : "45";
    const goalTurn = goalAvgTurnDays != null ? String(Math.round(goalAvgTurnDays)) : turn;
    // Mode B: prefer goal monthly sales, then current avg, then fallback
    const salesPerMonth = (goalAvgSalesPerMonth ?? avgSalesPerMonth) != null
      ? String(Math.round((goalAvgSalesPerMonth ?? avgSalesPerMonth)!))
      : "20";

    const e = userEdited.current;
    if (!e.has("unitsInStock")) setUnitsInStock(stock);
    if (!e.has("modeATurnDays")) setModeATurnDays(turn);
    if (!e.has("targetUnitsPerMonth")) setTargetUnitsPerMonth(salesPerMonth);
    if (!e.has("modeBTurnDays")) setModeBTurnDays(turn);
    if (!e.has("impactStock")) setImpactStock(stock);
    if (!e.has("impactCurrentTurn")) setImpactCurrentTurn(turn);
    if (!e.has("impactGoalTurn")) setImpactGoalTurn(goalTurn);
  }, [avgUnitsOnLot, profileAvgTurnDays, autoAvgTurnDays, avgSalesPerMonth, goalAvgSalesPerMonth, goalAvgTurnDays]);

  // Mode A calculations: units sold = stock × 365 / turnDays
  const modeAStock = nf(unitsInStock);
  const modeATurn = nf(modeATurnDays) > 0 ? nf(modeATurnDays) : (autoAvgTurnDays ?? 45);
  const modeAUnitsPerYear = modeAStock > 0 && modeATurn > 0 ? Math.round(modeAStock * 365 / modeATurn) : null;
  const modeAUnitsPerMonth = modeAUnitsPerYear != null ? Math.round(modeAUnitsPerYear / 12) : null;

  // Mode B calculations: stock needed = targetUnitsPerYear × turnDays / 365
  const modeBMonthly = nf(targetUnitsPerMonth);
  const modeBTurn = nf(modeBTurnDays) > 0 ? nf(modeBTurnDays) : (autoAvgTurnDays ?? 45);
  const modeBUnitsPerYear = modeBMonthly > 0 ? Math.round(modeBMonthly * 12) : null;
  // Formula: (target sales per month / turns per year) × 12
  // turnsPerYear = 365 / turnDays
  const modeBTurnsPerYear = modeBTurn > 0 ? 365 / modeBTurn : null;
  const modeBStock = modeBMonthly > 0 && modeBTurnsPerYear != null
    ? Math.ceil((modeBMonthly / modeBTurnsPerYear) * 12)
    : null;

  // Turn Time Impact calculations
  const impactStockNum = nf(impactStock);
  const impactCurrentNum = nf(impactCurrentTurn);
  const impactGoalNum = nf(impactGoalTurn);
  const impactCurrentPerYear = impactStockNum > 0 && impactCurrentNum > 0 ? Math.round(impactStockNum * 365 / impactCurrentNum) : null;
  const impactGoalPerYear = impactStockNum > 0 && impactGoalNum > 0 ? Math.round(impactStockNum * 365 / impactGoalNum) : null;
  const extraUnitsPerYear = impactCurrentPerYear != null && impactGoalPerYear != null ? impactGoalPerYear - impactCurrentPerYear : null;
  const extraUnitsPerMonth = extraUnitsPerYear != null ? Math.round(extraUnitsPerYear / 12) : null;
  const extraAnnualGrossProfit = extraUnitsPerYear != null && gpuForImpact != null ? extraUnitsPerYear * gpuForImpact : null;
  const extraAnnualRevenue = extraUnitsPerYear != null && avgVehicleValue != null ? extraUnitsPerYear * avgVehicleValue : null;
  const impactInputsReady = impactStockNum > 0 && impactCurrentNum > 0 && impactGoalNum > 0;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Car className="w-4 h-4 text-primary" />
          <CardTitle className="text-base">{t("pages:sla.planner.title")}</CardTitle>
          <Badge variant="secondary" className="text-xs">{t("pages:sla.planner.badge")}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="a">
          <TabsList className="h-8 text-xs">
            <TabsTrigger value="a" className="text-xs" data-testid="tab-planner-mode-a">{t("pages:sla.planner.tabSelling")}</TabsTrigger>
            <TabsTrigger value="b" className="text-xs" data-testid="tab-planner-mode-b">{t("pages:sla.planner.tabOnLot")}</TabsTrigger>
          </TabsList>

          {/* Mode A: stock → projected sales */}
          <TabsContent value="a" className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground">{t("pages:sla.planner.modeADesc")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t("pages:sla.planner.unitsForSaleOnLot")}</Label>
                <Input
                  type="number"
                  placeholder="50"
                  value={unitsInStock}
                  onChange={(e) => { userEdited.current.add("unitsInStock"); setUnitsInStock(e.target.value); }}
                  data-testid="input-planner-units-in-stock"
                />
                {avgUnitsOnLot != null && (
                  <p className="text-xs text-muted-foreground">{t("pages:sla.planner.profileAvg", { value: fmtNum(avgUnitsOnLot, 0) })}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t("pages:sla.planner.avgTurnTime")}</Label>
                <Input
                  type="number"
                  placeholder="45"
                  value={modeATurnDays}
                  onChange={(e) => { userEdited.current.add("modeATurnDays"); setModeATurnDays(e.target.value); }}
                  data-testid="input-planner-mode-a-turn-days"
                />
                {profileAvgTurnDays != null
                  ? <p className="text-xs text-muted-foreground">{t("pages:sla.planner.fromProfile", { value: fmtNum(profileAvgTurnDays, 0) })}</p>
                  : autoAvgTurnDays != null
                    ? <p className="text-xs text-muted-foreground">{t("pages:sla.planner.computedFloorplan", { value: fmtNum(autoAvgTurnDays, 0) })}</p>
                    : null}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                  <span className="text-sm text-muted-foreground">{t("pages:sla.planner.projectedUnitsYear")}</span>
                  <p className="text-base font-bold tabular-nums" data-testid="result-planner-units-per-year">
                    {modeAUnitsPerYear != null ? fmtNum(modeAUnitsPerYear, 0) : "—"}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-primary/8 border border-primary/20">
                  <span className="text-sm text-muted-foreground">{t("pages:sla.planner.projectedUnitsMonth")}</span>
                  <p className="text-base font-bold tabular-nums text-primary" data-testid="result-planner-units-per-month">
                    {modeAUnitsPerMonth != null ? fmtNum(modeAUnitsPerMonth, 0) : "—"}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Mode B: target sales → stock needed */}
          <TabsContent value="b" className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground">{t("pages:sla.planner.modeBDesc")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t("pages:sla.planner.targetUnitsPerMonth")}</Label>
                <Input
                  type="number"
                  placeholder="20"
                  value={targetUnitsPerMonth}
                  onChange={(e) => { userEdited.current.add("targetUnitsPerMonth"); setTargetUnitsPerMonth(e.target.value); }}
                  data-testid="input-planner-target-units-per-month"
                />
                {goalAvgSalesPerMonth != null && (
                  <p className="text-xs text-muted-foreground">{t("pages:sla.planner.salesGoal", { value: fmtNum(goalAvgSalesPerMonth, 0) })}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t("pages:sla.planner.avgTurnTime")}</Label>
                <Input
                  type="number"
                  placeholder="45"
                  value={modeBTurnDays}
                  onChange={(e) => { userEdited.current.add("modeBTurnDays"); setModeBTurnDays(e.target.value); }}
                  data-testid="input-planner-mode-b-turn-days"
                />
                {profileAvgTurnDays != null
                  ? <p className="text-xs text-muted-foreground">{t("pages:sla.planner.fromProfile", { value: fmtNum(profileAvgTurnDays, 0) })}</p>
                  : autoAvgTurnDays != null
                    ? <p className="text-xs text-muted-foreground">{t("pages:sla.planner.computedFloorplan", { value: fmtNum(autoAvgTurnDays, 0) })}</p>
                    : null}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                  <span className="text-sm text-muted-foreground">{t("pages:sla.planner.targetUnitsYear")}</span>
                  <p className="text-base font-bold tabular-nums" data-testid="result-planner-units-per-year-b">
                    {modeBUnitsPerYear != null ? fmtNum(modeBUnitsPerYear, 0) : "—"}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-primary/8 border border-primary/20">
                  <span className="text-sm text-muted-foreground">{t("pages:sla.planner.unitsNeededOnLot")}</span>
                  <p className="text-base font-bold tabular-nums text-primary" data-testid="result-planner-units-to-stock">
                    {modeBStock != null ? fmtNum(modeBStock, 0) : "—"}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Turn Time Impact — collapsible */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/30 transition-colors"
            onClick={() => setShowImpact((s) => !s)}
            data-testid="button-toggle-turn-impact"
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span>{t("pages:sla.planner.turnTimeImpact")}</span>
              <Badge variant="secondary" className="text-xs">{t("pages:sla.planner.whatIfFaster")}</Badge>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showImpact ? "rotate-180" : ""}`} />
          </button>

          {showImpact && (
            <div className="border-t border-border px-4 pb-4 pt-4 space-y-4">
              <p className="text-xs text-muted-foreground">{t("pages:sla.planner.impactDesc")}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{t("pages:sla.planner.unitsOnLot")}</Label>
                  <Input
                    type="number"
                    placeholder="50"
                    value={impactStock}
                    onChange={(e) => { userEdited.current.add("impactStock"); setImpactStock(e.target.value); }}
                    data-testid="input-impact-stock"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{t("pages:sla.planner.currentTurn")}</Label>
                  <Input
                    type="number"
                    placeholder="45"
                    value={impactCurrentTurn}
                    onChange={(e) => { userEdited.current.add("impactCurrentTurn"); setImpactCurrentTurn(e.target.value); }}
                    data-testid="input-impact-current-turn"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{t("pages:sla.planner.goalTurn")}</Label>
                  <Input
                    type="number"
                    placeholder="30"
                    value={impactGoalTurn}
                    onChange={(e) => { userEdited.current.add("impactGoalTurn"); setImpactGoalTurn(e.target.value); }}
                    data-testid="input-impact-goal-turn"
                  />
                </div>
              </div>

              {(impactCurrentNum > 0 || impactInputsReady) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                  {impactCurrentNum > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                      <span className="text-sm text-muted-foreground">{t("pages:sla.planner.turnsCurrent")}</span>
                      <p className="text-base font-bold tabular-nums" data-testid="result-impact-turns-current">
                        {fmtNum(365 / impactCurrentNum, 1)}×
                      </p>
                    </div>
                  )}
                  {impactGoalNum > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                      <span className="text-sm text-muted-foreground">{t("pages:sla.planner.turnsGoal")}</span>
                      <p className="text-base font-bold tabular-nums" data-testid="result-impact-turns-goal">
                        {fmtNum(365 / impactGoalNum, 1)}×
                      </p>
                    </div>
                  )}
                </div>
              )}
              {impactInputsReady && extraUnitsPerYear != null ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                    <span className="text-sm text-muted-foreground">{t("pages:sla.planner.extraUnitsYear")}</span>
                    <p
                      className={`text-base font-bold tabular-nums ${extraUnitsPerYear >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500"}`}
                      data-testid="result-impact-extra-per-year"
                    >
                      {extraUnitsPerYear >= 0 ? "+" : ""}{fmtNum(extraUnitsPerYear, 0)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                    <span className="text-sm text-muted-foreground">{t("pages:sla.planner.extraUnitsMonth")}</span>
                    <p
                      className={`text-base font-bold tabular-nums ${(extraUnitsPerMonth ?? 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500"}`}
                      data-testid="result-impact-extra-per-month"
                    >
                      {(extraUnitsPerMonth ?? 0) >= 0 ? "+" : ""}{fmtNum(extraUnitsPerMonth ?? 0, 0)}
                    </p>
                  </div>
                  {extraAnnualGrossProfit != null && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                      <span className="text-sm text-muted-foreground">{t("pages:sla.planner.extraGrossProfit")}</span>
                      <p
                        className={`text-base font-bold tabular-nums ${extraAnnualGrossProfit >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500"}`}
                        data-testid="result-impact-extra-gross-profit"
                      >
                        {extraAnnualGrossProfit >= 0 ? "+" : ""}{fmt$(extraAnnualGrossProfit)}
                      </p>
                    </div>
                  )}
                  {extraAnnualRevenue != null && (
                    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted/40">
                      <span className="text-sm text-muted-foreground">{t("pages:sla.planner.extraRevenue")}</span>
                      <p
                        className={`text-base font-bold tabular-nums ${extraAnnualRevenue >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500"}`}
                        data-testid="result-impact-extra-revenue"
                      >
                        {extraAnnualRevenue >= 0 ? "+" : ""}{fmt$(extraAnnualRevenue)}
                      </p>
                    </div>
                  )}
                </div>
              ) : !impactInputsReady ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-muted/30 text-sm text-muted-foreground">
                  <Info className="w-4 h-4 shrink-0 opacity-50" />
                  {t("pages:sla.planner.fillAllThree")}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Data Schedule & Health ────────────────────────────────────────────────────

const DATA_TYPES: { key: DataSlaType; label: string; description: string }[] = [
  { key: "receivables", label: "Receivables", description: "Lender/floorplan statements" },
  { key: "p_and_l", label: "P&L Statement", description: "Profit & loss financial data" },
  { key: "bank_statement", label: "Bank Statement", description: "Bank account statements" },
  { key: "balance_sheet", label: "Balance Sheet", description: "Assets & liabilities snapshot" },
  { key: "inventory", label: "Inventory", description: "Vehicle inventory listings" },
];

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "manual", label: "Manual / Off" },
];

const WEEKDAY_OPTIONS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const MONTHLY_DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: i === 0 ? "1st" : i === 1 ? "2nd" : i === 2 ? "3rd" : `${i + 1}th`,
}));

interface SlaRow {
  dataType: DataSlaType;
  frequency: "weekly" | "monthly" | "quarterly" | "manual";
  dayTarget: number | null;
  isEnabled: boolean;
}

function defaultRows(): SlaRow[] {
  return DATA_TYPES.map(({ key }) => ({
    dataType: key,
    frequency: "monthly",
    dayTarget: 15,
    isEnabled: false,
  }));
}

function DataScheduleCard() {
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: existing, isLoading } = useQuery<DealerDataSla[]>({
    queryKey: ["/api/dealer/data-sla"],
    staleTime: 30_000,
  });

  const [rows, setRows] = useState<SlaRow[]>(defaultRows);
  const [dirty, setDirty] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!existing || initialized) return;
    const merged = DATA_TYPES.map(({ key }) => {
      const found = existing.find((e) => e.dataType === key);
      if (found) {
        return {
          dataType: key,
          frequency: found.frequency as SlaRow["frequency"],
          dayTarget: found.dayTarget ?? null,
          isEnabled: found.isEnabled,
        };
      }
      return { dataType: key, frequency: "monthly" as const, dayTarget: 15, isEnabled: false };
    });
    setRows(merged);
    setInitialized(true);
  }, [existing, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/dealer/data-sla", { items: rows }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer/data-sla"] });
      qc.invalidateQueries({ queryKey: ["/api/alerts"] });
      setDirty(false);
      toast({ title: t("pages:sla.dataSchedule.scheduleSaved"), description: t("pages:sla.dataSchedule.scheduleSavedDesc") });
    },
    onError: () => toast({ title: t("pages:sla.dataSchedule.saveFailed"), variant: "destructive" }),
  });

  function updateRow(idx: number, patch: Partial<SlaRow>) {
    setRows((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...patch };
      return updated;
    });
    setDirty(true);
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10 flex justify-center items-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-data-schedule">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Calendar className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold leading-tight">{t("pages:sla.dataSchedule.title")}</CardTitle>
            <CardDescription className="text-xs mt-0.5">{t("pages:sla.dataSchedule.description")}</CardDescription>
          </div>
          {dirty && (
            <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-data-schedule">
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {t("pages:sla.common.save")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {DATA_TYPES.map(({ key }, idx) => {
          const label = t(`pages:sla.dataSchedule.types.${key}`);
          const description = t(`pages:sla.dataSchedule.types.${key}Desc`);
          const row = rows[idx];
          if (!row) return null;
          return (
            <div
              key={key}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border transition-colors ${row.isEnabled ? "border-primary/20 bg-primary/5" : "border-border bg-muted/20"}`}
              data-testid={`sla-row-${key}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Switch
                  checked={row.isEnabled}
                  onCheckedChange={(v) => updateRow(idx, { isEnabled: v })}
                  data-testid={`switch-sla-${key}`}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{label}</p>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
              </div>
              {row.isEnabled && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Select
                    value={row.frequency}
                    onValueChange={(v) => updateRow(idx, { frequency: v as SlaRow["frequency"], dayTarget: v === "weekly" ? 1 : v === "monthly" ? 15 : 15 })}
                  >
                    <SelectTrigger className="h-8 text-xs w-32" data-testid={`select-frequency-${key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.filter((o) => o.value !== "manual").map((o) => (
                        <SelectItem key={o.value} value={o.value}>{t(`pages:sla.dataSchedule.frequency.${o.value}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {row.frequency === "weekly" && (
                    <Select
                      value={String(row.dayTarget ?? 1)}
                      onValueChange={(v) => updateRow(idx, { dayTarget: Number(v) })}
                    >
                      <SelectTrigger className="h-8 text-xs w-36" data-testid={`select-day-${key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{t("pages:sla.dataSchedule.byLabel", { label: t(`pages:sla.dataSchedule.weekdays.${o.value}`) })}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {(row.frequency === "monthly" || row.frequency === "quarterly") && (
                    <Select
                      value={String(row.dayTarget ?? 15)}
                      onValueChange={(v) => updateRow(idx, { dayTarget: Number(v) })}
                    >
                      <SelectTrigger className="h-8 text-xs w-28" data-testid={`select-day-${key}`}>
                        <SelectValue placeholder={t("pages:sla.dataSchedule.dayOfMonth")} />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHLY_DAY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{t("pages:sla.dataSchedule.byLabel", { label: o.label })}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!dirty && !saveMutation.isPending && (
          <p className="text-xs text-muted-foreground pt-1">{t("pages:sla.dataSchedule.toggleHint")}</p>
        )}
      </CardContent>
    </Card>
  );
}

interface DataSlaAlertItem {
  type: string;
  unitLabel: string;
  detail: string;
  link: string;
  severity: "warning" | "error" | "critical";
  dataType?: string;
  daysOverdue?: number;
}

function DataHealthCard() {
  const { t } = useTranslation(["pages", "common"]);
  const { data: alertData } = useQuery<{ alerts: DataSlaAlertItem[] }>({
    queryKey: ["/api/alerts"],
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const slaAlerts = (alertData?.alerts ?? []).filter((a) => a.type === "data_overdue");

  const { data: slaRules } = useQuery<DealerDataSla[]>({
    queryKey: ["/api/dealer/data-sla"],
    staleTime: 30_000,
  });

  const enabledRules = (slaRules ?? []).filter((r) => r.isEnabled && r.frequency !== "manual");

  if (enabledRules.length === 0) return null;

  const criticalCount = slaAlerts.filter((a) => a.severity === "critical").length;
  const errorCount = slaAlerts.filter((a) => a.severity === "error").length;
  const warningCount = slaAlerts.filter((a) => a.severity === "warning").length;
  const onTimeCount = enabledRules.length - slaAlerts.length;

  const overallStatus = criticalCount > 0 ? "critical" : errorCount > 0 ? "error" : warningCount > 0 ? "warning" : "ok";

  return (
    <Card
      className={`border-l-4 ${overallStatus === "critical" ? "border-l-red-500" : overallStatus === "error" ? "border-l-destructive" : overallStatus === "warning" ? "border-l-amber-400" : "border-l-emerald-500"}`}
      data-testid="card-data-health"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${overallStatus === "ok" ? "bg-emerald-100 dark:bg-emerald-950/40" : overallStatus === "critical" ? "bg-red-100 dark:bg-red-950/40" : "bg-amber-100 dark:bg-amber-950/40"}`}>
            {overallStatus === "ok" ? (
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <ShieldAlert className={`w-3.5 h-3.5 ${overallStatus === "critical" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold leading-tight">{t("pages:sla.dataHealth.title")}</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              {overallStatus === "ok" ? t("pages:sla.dataHealth.allCurrent") : t("pages:sla.dataHealth.overdue", { count: slaAlerts.length })}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            {onTimeCount > 0 && <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 text-xs">{t("pages:sla.dataHealth.onTime", { count: onTimeCount })}</Badge>}
            {warningCount > 0 && <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 text-xs">{t("pages:sla.dataHealth.late", { count: warningCount })}</Badge>}
            {errorCount > 0 && <Badge variant="destructive" className="text-xs">{t("pages:sla.dataHealth.overdueBadge", { count: errorCount })}</Badge>}
            {criticalCount > 0 && <Badge className="bg-red-600 text-white text-xs">{t("pages:sla.dataHealth.critical", { count: criticalCount })}</Badge>}
          </div>
        </div>
      </CardHeader>
      {slaAlerts.length > 0 && (
        <CardContent className="pt-0 space-y-2">
          {slaAlerts.map((alert, idx) => (
            <Link key={idx} href={alert.link}>
              <div
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer hover:bg-muted/40 transition-colors ${alert.severity === "critical" ? "border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/20" : alert.severity === "error" ? "border-destructive/30 bg-destructive/5" : "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20"}`}
                data-testid={`data-health-breach-${alert.dataType ?? idx}`}
              >
                <div className="mt-0.5 shrink-0">
                  {alert.severity === "critical" ? (
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                  ) : alert.severity === "error" ? (
                    <XCircle className="w-4 h-4 text-destructive" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{alert.unitLabel}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{alert.detail}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Upload className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{t("pages:sla.dataHealth.upload")}</span>
                </div>
              </div>
            </Link>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SlaPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: saved, isFetching: opAnalyticsFetching } = useOpAnalytics();
  const { data: statements = [] } = useQuery<unknown[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: lenders = [] } = useQuery<{ id: string; name: string }[]>({ queryKey: ["/api/lenders"] });
  const { data: inventoryItems = [], isSuccess: inventoryLoaded } = useQuery<{ saleDate?: string | null }[]>({ queryKey: ["/api/inventory"] });
  const { data: profile } = useQuery<DealerProfile | null>({
    queryKey: ["/api/dealer-profile"],
    staleTime: 60_000,
    retry: false,
  });

  // ── Incomplete profile banner dismissal ───────────────────────────────────
  const [incompleteProfileDismissed, setIncompleteProfileDismissed] = useState(
    () => localStorage.getItem("dealerProfileIncompleteDismissed") === "true"
  );

  function dismissIncompleteProfileBanner() {
    localStorage.setItem("dealerProfileIncompleteDismissed", "true");
    setIncompleteProfileDismissed(true);
  }

  // ── Alert Settings state ───────────────────────────────────────────────────
  const [alertEmail, setAlertEmail] = useState("");
  const [alertCustomAgingDays, setAlertCustomAgingDays] = useState("");
  const [alertMaxHoldingCost, setAlertMaxHoldingCost] = useState("");
  const [alertPriceStagnationDays, setAlertPriceStagnationDays] = useState("");
  const [alertAgingEnabled, setAlertAgingEnabled] = useState(true);
  const [alertHoldingCostEnabled, setAlertHoldingCostEnabled] = useState(true);
  const [alertPriceStagnationEnabled, setAlertPriceStagnationEnabled] = useState(true);
  const [alertSettingsLoaded, setAlertSettingsLoaded] = useState(false);

  useEffect(() => {
    if (profile && !alertSettingsLoaded) {
      setAlertEmail(profile.notifyEmail ?? "");
      setAlertCustomAgingDays(profile.alertCustomAgingDays != null ? String(profile.alertCustomAgingDays) : "");
      setAlertMaxHoldingCost(profile.goalMaxHoldingCost != null ? String(Math.round(parseFloat(String(profile.goalMaxHoldingCost)))) : "");
      setAlertPriceStagnationDays(profile.goalPriceStagnationDays != null ? String(profile.goalPriceStagnationDays) : "");
      setAlertAgingEnabled(profile.alertAgingEnabled !== false);
      setAlertHoldingCostEnabled(profile.alertHoldingCostEnabled !== false);
      setAlertPriceStagnationEnabled(profile.alertPriceStagnationEnabled !== false);
      setAlertSettingsLoaded(true);
    }
  }, [profile, alertSettingsLoaded]);

  const saveAlertSettingsMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/alerts/preferences", {
        notifyEmail: alertEmail,
        alertCustomAgingDays: alertCustomAgingDays.trim() ? parseInt(alertCustomAgingDays, 10) : null,
        goalMaxHoldingCost: alertMaxHoldingCost.trim() ? parseFloat(alertMaxHoldingCost.replace(/,/g, "")) : null,
        goalPriceStagnationDays: alertPriceStagnationDays.trim() ? parseInt(alertPriceStagnationDays, 10) : null,
        alertAgingEnabled,
        alertHoldingCostEnabled,
        alertPriceStagnationEnabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      toast({ title: t("pages:sla.page.alertSettingsSaved") });
    },
    onError: () => {
      toast({ title: t("pages:sla.page.failedSaveAlertSettings"), variant: "destructive" });
    },
  });

  const linkedStatements = useMemo(
    () => filterStatementsToLenders(
      statements as Parameters<typeof filterStatementsToLenders>[0],
      lenders,
    ),
    [statements, lenders],
  );

  const inpData = (saved as Record<string, unknown> | undefined)?.input as Record<string, unknown> | null ?? null;
  const sourceTaxYear: number | null = (saved as Record<string, unknown> | undefined)?.sourceTaxYear != null
    ? Number((saved as Record<string, unknown>).sourceTaxYear)
    : null;

  // ── Actuals from operational analytics ────────────────────────────────────

  const currentGrossProfit: number | null = inpData?.grossProfit != null ? Number(inpData.grossProfit) : null;
  // Prefer profile monthly sales × 12 (from SLA current metrics), fallback to op-analytics stored value
  const profileMonthlyUnits = profile?.avgSalesPerMonth != null ? nf(String(profile.avgSalesPerMonth)) : null;
  const currentAnnualUnitsSold: number | null =
    profileMonthlyUnits != null && profileMonthlyUnits > 0
      ? profileMonthlyUnits * 12
      : inpData?.annualUnitsSold != null ? Number(inpData.annualUnitsSold) : null;
  const currentAnnualTotalExpenses: number | null = inpData?.annualTotalExpenses != null ? Number(inpData.annualTotalExpenses) : null;
  const currentNetProfit: number | null =
    currentGrossProfit != null && currentAnnualTotalExpenses != null
      ? currentGrossProfit - currentAnnualTotalExpenses : null;
  const currentGrossProfitPerUnit: number | null =
    inpData?.avgGrossProfitPerUnit != null
      ? Number(inpData.avgGrossProfitPerUnit)
      : currentGrossProfit != null && (currentAnnualUnitsSold ?? 0) > 0
        ? currentGrossProfit / currentAnnualUnitsSold!
        : null;
  const currentMonthlyRevenue: number | null = inpData?.revenue != null ? Number(inpData.revenue) / 12 : null;
  const currentMonthlyExpenses: number | null = currentAnnualTotalExpenses != null ? currentAnnualTotalExpenses / 12 : null;
  const currentMonthlySales: number | null = currentAnnualUnitsSold != null ? currentAnnualUnitsSold / 12 : null;
  const autoStats = useMemo(() => computeFloorplanStats(linkedStatements as Parameters<typeof computeFloorplanStats>[0]), [linkedStatements]);
  const autoAvgTurnDays = autoStats.avgTurnDays;
  const autoAvgVehicleValue = autoStats.avgVehicleValue;

  const liveUnitsOnLot = useMemo(
    () => inventoryItems.filter((item) => item.saleDate == null).length,
    [inventoryItems],
  );

  const currentAvgTurnDays: number =
    ((inpData?.avgTurnDaysWasOverride ? inpData.avgTurnDays : null) as number | null) ??
    autoAvgTurnDays ??
    (inpData?.avgTurnDays as number | null) ??
    45;

  // ── Goals from profile ────────────────────────────────────────────────────

  const goalMonthlySales = profile?.goalAvgSalesPerMonth != null ? nf(profile.goalAvgSalesPerMonth) : null;
  const goalGPU = profile?.goalAvgGrossProfitPerUnit != null ? nf(profile.goalAvgGrossProfitPerUnit) : null;
  const goalAvgTurnDays = profile?.goalAvgTurnDays != null ? nf(profile.goalAvgTurnDays) : null;
  const goalMonthlyRevenue = profile?.goalAvgMonthlyRevenue != null ? nf(profile.goalAvgMonthlyRevenue) : null;
  const goalMonthlyOpEx = profile?.goalMonthlyOperatingExpenses != null ? nf(profile.goalMonthlyOperatingExpenses) : null;
  const goalAvgUnitsOnLot = profile?.goalAvgUnitsOnLot != null ? nf(profile.goalAvgUnitsOnLot) : null;

  const hasActuals = currentGrossProfit != null || currentAnnualUnitsSold != null;
  const hasGoals = goalMonthlySales != null || goalGPU != null || goalMonthlyRevenue != null;

  // ── Real-value annotations for Dealership Metrics cards ──────────────────
  const realValuesMap = useMemo((): RealValuesMap => {
    const map: RealValuesMap = {};
    if (inventoryLoaded) {
      map.avgUnitsOnLot = { value: liveUnitsOnLot, source: t("pages:sla.page.sourceFromInventory") };
    }
    // Only annotate "from tax return" when the saved analytics actually came
    // from an uploaded tax return (sourceTaxYear present). Manually entered /
    // onboarding-derived inputs must not be labeled as tax-return data.
    if (sourceTaxYear) {
      const taxReturnSource = t("pages:sla.page.sourceFromTaxReturnYear", { year: sourceTaxYear });
      if (!opAnalyticsFetching && currentGrossProfitPerUnit != null) {
        map.avgGrossProfitPerUnit = { value: currentGrossProfitPerUnit, source: taxReturnSource };
      }
      if (!opAnalyticsFetching && currentMonthlyRevenue != null) {
        map.avgMonthlyRevenue = { value: currentMonthlyRevenue, source: taxReturnSource };
      }
      if (!opAnalyticsFetching && currentMonthlyExpenses != null) {
        map.monthlyOperatingExpenses = { value: currentMonthlyExpenses, source: taxReturnSource };
      }
    }
    if (autoAvgVehicleValue != null) {
      map.avgVehicleValue = { value: autoAvgVehicleValue, source: t("pages:sla.page.sourceFromInventory") };
    }
    if (autoAvgTurnDays != null) {
      map.avgTurnDays = { value: autoAvgTurnDays, source: t("pages:sla.page.sourceFromReceivables") };
    }
    return map;
  }, [inventoryLoaded, liveUnitsOnLot, currentGrossProfitPerUnit, autoAvgVehicleValue, currentMonthlyRevenue, currentMonthlyExpenses, autoAvgTurnDays, sourceTaxYear, opAnalyticsFetching]);

  // ── Scenario objects — profile is primary source for "current"; op-analytics is fallback ──

  // Profile-based current values (from dealer onboarding profile)
  const profileGPU: number | null = profile?.avgGrossProfitPerUnit != null ? nf(profile.avgGrossProfitPerUnit) : null;
  const profileMonthlyOpEx: number | null = profile?.monthlyOperatingExpenses != null ? nf(profile.monthlyOperatingExpenses) : null;
  const profileMonthlySales: number | null = profile?.avgSalesPerMonth != null ? nf(profile.avgSalesPerMonth) : null;
  const profileMonthlyRevenue: number | null = profile?.avgMonthlyRevenue != null ? nf(profile.avgMonthlyRevenue) : null;
  const profileAvgVehicleValue: number | null = profile?.avgVehicleValue != null ? nf(profile.avgVehicleValue) : null;
  const profileStatedTurnDays: number | null = profile?.avgTurnDays != null ? nf(profile.avgTurnDays) : null;
  const profileReconDays: number = profile?.avgReconDays != null ? nf(profile.avgReconDays) : 0;

  const beGPU = profileGPU ?? currentGrossProfitPerUnit;
  const beMonthlyOpEx = profileMonthlyOpEx ?? currentMonthlyExpenses;
  const statedAnnualOpEx = beMonthlyOpEx != null ? beMonthlyOpEx * 12 : currentAnnualTotalExpenses;

  const pageBreakEvenUnits: number | null =
    (beGPU ?? 0) > 0 && (beMonthlyOpEx ?? 0) > 0
      ? Math.ceil(beMonthlyOpEx! / beGPU!)
      : null;

  const currentScenario: Scenario = {
    label: "Current",
    gpu: profileGPU ?? currentGrossProfitPerUnit,
    avgVehicleValue: profileAvgVehicleValue ?? autoAvgVehicleValue,
    monthlyOpEx: profileMonthlyOpEx ?? currentMonthlyExpenses,
    monthlyUnits: profileMonthlySales ?? currentMonthlySales,
    turnDays: profileStatedTurnDays ?? currentAvgTurnDays,
  };

  const goalScenario: Scenario = {
    label: "Goal",
    gpu: goalGPU,
    // Fall back to current vehicle value so BE revenue shows even when no goal vehicle value is set
    avgVehicleValue: profileAvgVehicleValue ?? autoAvgVehicleValue,
    monthlyOpEx: goalMonthlyOpEx,
    monthlyUnits: goalMonthlySales,
    turnDays: goalAvgTurnDays,
  };

  // ── Goal status summary ───────────────────────────────────────────────────

  const goalRowDefs = useMemo(() => [
    { actual: currentMonthlySales, goal: goalMonthlySales, higherIsBetter: true },
    { actual: currentGrossProfitPerUnit, goal: goalGPU, higherIsBetter: true },
    { actual: currentMonthlyRevenue, goal: goalMonthlyRevenue, higherIsBetter: true },
    { actual: currentMonthlyExpenses, goal: goalMonthlyOpEx, higherIsBetter: false },
    { actual: autoAvgTurnDays, goal: goalAvgTurnDays, higherIsBetter: false },
  ].filter((r) => r.goal != null && r.goal !== 0), [
    currentMonthlySales, goalMonthlySales, currentGrossProfitPerUnit, goalGPU,
    currentMonthlyRevenue, goalMonthlyRevenue, currentMonthlyExpenses, goalMonthlyOpEx,
    autoAvgTurnDays, goalAvgTurnDays,
  ]);

  const summary = useMemo(() => {
    let green = 0, yellow = 0, red = 0;
    for (const r of goalRowDefs) {
      const color = statusColor(pctOf(r.actual, r.goal), r.higherIsBetter);
      if (color === "green") green++;
      else if (color === "yellow") yellow++;
      else red++;
    }
    return { green, yellow, red };
  }, [goalRowDefs]);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-screen-xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("pages:sla.page.title")}</h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          {t("pages:sla.page.subtitle")}
        </p>
      </div>

      <Tabs defaultValue="current">
        <TabsList className="mb-4">
          <TabsTrigger value="current" data-testid="tab-current">{t("pages:sla.page.tabCurrent")}</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals">{t("pages:sla.page.tabGoals")}</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data-schedule">{t("pages:sla.page.tabAlertSchedule")}</TabsTrigger>
        </TabsList>

        {/* ── Current Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="current" className="space-y-6">
          {/* Profile completeness prompt — opens wizard directly */}
          {profile && !isDealerProfileComplete(profile) && !incompleteProfileDismissed && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">{t("pages:sla.page.profileIncomplete")}</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">{t("pages:sla.page.profileIncompleteDesc")}</p>
              </div>
              <Link href="/account">
                <Button size="sm" variant="outline" className="shrink-0 text-xs" data-testid="button-complete-onboarding">
                  {t("pages:sla.page.completeProfile")} <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
              <button
                onClick={dismissIncompleteProfileBanner}
                className="shrink-0 text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors"
                data-testid="button-dismiss-incomplete-profile"
                aria-label={t("pages:sla.page.dismissBanner")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {!profile && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">{t("pages:sla.page.noProfile")}</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">{t("pages:sla.page.noProfileDesc")}</p>
              </div>
              <Link href="/account">
                <Button size="sm" variant="outline" className="shrink-0 text-xs" data-testid="button-setup-profile">
                  {t("pages:sla.page.setUpProfile")} <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          )}

          <CurrentProfileEditor profile={profile ?? null} autoAvgTurnDays={autoAvgTurnDays} realValues={realValuesMap} />

          {/* Stocking recommendation — uses profile stated values */}
          {profileMonthlySales != null && profileMonthlySales > 0 && profileStatedTurnDays != null && profileStatedTurnDays > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-500/8 border border-blue-500/20">
              <Car className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0" />
              <p className="text-sm" data-testid="text-stocking-insight">
                {t("pages:sla.page.stockingPrefix")}{" "}
                <span className="font-bold">{t("pages:sla.page.stockingDays", { days: Math.round(profileStatedTurnDays) })}</span>
                {profileReconDays > 0 && (
                  <>{t("pages:sla.page.stockingReconMid")} <span className="font-bold">{t("pages:sla.page.stockingDays", { days: Math.round(profileReconDays) })}</span></>
                )}
                {t("pages:sla.page.stockingSalesMid")}{" "}
                <span className="font-bold">{t("pages:sla.page.stockingUnitsPerMonth", { units: Math.round(profileMonthlySales) })}</span> {t("pages:sla.page.stockingSalesMid2")}{" "}
                <span className="font-bold">
                  {Math.ceil((profileMonthlySales / 30.4) * (profileStatedTurnDays + profileReconDays))}
                </span>{" "}
                {t("pages:sla.page.stockingSuffix")}
              </p>
            </div>
          )}

          {pageBreakEvenUnits != null && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/8 border border-amber-500/20" data-testid="text-breakeven-statement">
              <Receipt className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
              <p className="text-sm">
                {t("pages:sla.page.breakevenPrefix")}{" "}
                <span className="font-bold">{pageBreakEvenUnits}</span> {t("pages:sla.page.breakevenSuffix")}
              </p>
            </div>
          )}

          {/* Inventory Planner — bidirectional turn-time calculator */}
          <TurnTimePlannerCard
            autoAvgTurnDays={autoAvgTurnDays}
            profileAvgTurnDays={profileStatedTurnDays}
            goalAvgTurnDays={goalAvgTurnDays}
            avgUnitsOnLot={profile?.avgUnitsOnLot != null ? nf(profile.avgUnitsOnLot) || null : null}
            avgSalesPerMonth={profile?.avgSalesPerMonth != null ? nf(profile.avgSalesPerMonth) || null : null}
            goalAvgSalesPerMonth={goalMonthlySales}
            gpuForImpact={profileGPU ?? currentGrossProfitPerUnit}
            avgVehicleValue={profile?.avgVehicleValue != null ? nf(profile.avgVehicleValue) || null : null}
          />

          <Separator />

          {/* Goal Calculator */}
          <GrossGoalCalculator
            dataset="current"
            current={currentScenario}
            goal={goalScenario}
            currentAnnualTotalExpenses={statedAnnualOpEx}
            autoAvgTurnDays={autoAvgTurnDays}
          />

          <Separator />

          {/* Profit Simulator */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">{t("pages:sla.page.calculatorDataset")}</span>
            <span className="text-xs text-muted-foreground">{t("pages:sla.page.calculatorDatasetValue", { current: fmt$(beGPU ?? 0), goal: fmt$(goalScenario.gpu ?? 0) })}</span>
          </div>
          <ProfitSimulator
            current={currentScenario}
            goal={goalScenario}
          />

        </TabsContent>

        {/* ── Goals Tab ───────────────────────────────────────────────────── */}
        <TabsContent value="goals" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">{t("pages:sla.page.goalTracker")}</h2>
              {hasGoals && <SummaryBadges {...summary} />}
            </div>
            <GoalsEditor profile={profile ?? null} onSaved={() => {}} />
          </div>

          {!hasActuals && !hasGoals ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                <h3 className="font-semibold text-sm mb-1">{t("pages:sla.page.noGoalsTitle")}</h3>
                <p className="text-xs text-muted-foreground max-w-xs mx-auto mb-4">
                  {t("pages:sla.page.noGoalsDesc")}
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Link href="/operations">
                    <Button size="sm" variant="outline" data-testid="link-go-to-operations-goals">
                      {t("pages:sla.page.operationalAnalysis")} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <GoalRow label={t("pages:sla.page.goalRows.monthlySales")} actual={profileMonthlySales ?? currentMonthlySales} goal={goalMonthlySales} actualFormatted={(profileMonthlySales ?? currentMonthlySales) != null ? t("pages:sla.page.unitsValue", { value: fmtNum((profileMonthlySales ?? currentMonthlySales)!, 1) }) : "—"} goalFormatted={goalMonthlySales != null ? t("pages:sla.page.unitsValue", { value: fmtNum(goalMonthlySales, 0) }) : "—"} higherIsBetter formatGap={(g) => t("pages:sla.page.unitsValue", { value: fmtNum(g, 1) })} testId="actual-monthly-sales" />
              <GoalRow label={t("pages:sla.page.goalRows.grossPerUnit")} actual={profileGPU ?? currentGrossProfitPerUnit} goal={goalGPU} actualFormatted={fmt$(profileGPU ?? currentGrossProfitPerUnit)} goalFormatted={fmt$(goalGPU)} higherIsBetter formatGap={(g) => fmt$(g)} testId="actual-gross-per-unit" />
              <GoalRow label={t("pages:sla.page.goalRows.monthlyRevenue")} actual={profileMonthlyRevenue ?? currentMonthlyRevenue} goal={goalMonthlyRevenue} actualFormatted={fmt$(profileMonthlyRevenue ?? currentMonthlyRevenue)} goalFormatted={fmt$(goalMonthlyRevenue)} higherIsBetter formatGap={(g) => fmt$(g)} testId="actual-monthly-revenue" />
              <GoalRow label={t("pages:sla.page.goalRows.monthlyExpenses")} actual={profileMonthlyOpEx ?? currentMonthlyExpenses} goal={goalMonthlyOpEx} actualFormatted={fmt$(profileMonthlyOpEx ?? currentMonthlyExpenses)} goalFormatted={fmt$(goalMonthlyOpEx)} higherIsBetter={false} formatGap={(g) => fmt$(g)} testId="actual-monthly-expenses" />
              <GoalRow label={t("pages:sla.page.goalRows.avgTurnTime")} actual={profileStatedTurnDays ?? autoAvgTurnDays} goal={goalAvgTurnDays} actualFormatted={(profileStatedTurnDays ?? autoAvgTurnDays) != null ? t("pages:sla.page.daysValue", { value: fmtNum((profileStatedTurnDays ?? autoAvgTurnDays)!, 0) }) : "—"} goalFormatted={goalAvgTurnDays != null ? t("pages:sla.page.daysValue", { value: fmtNum(goalAvgTurnDays, 0) }) : "—"} higherIsBetter={false} formatGap={(g) => t("pages:sla.page.daysValue", { value: fmtNum(g, 1) })} testId="actual-avg-turn-days" exceedLabel="surpassed" />
              <GoalRow label={t("pages:sla.page.goalRows.avgUnitsOnLot")} actual={nf(profile?.avgUnitsOnLot) || null} goal={goalAvgUnitsOnLot} actualFormatted={profile?.avgUnitsOnLot != null ? t("pages:sla.page.unitsValue", { value: fmtNum(nf(profile.avgUnitsOnLot), 0) }) : "—"} goalFormatted={goalAvgUnitsOnLot != null ? t("pages:sla.page.unitsValue", { value: fmtNum(goalAvgUnitsOnLot, 0) }) : "—"} higherIsBetter formatGap={(g) => t("pages:sla.page.unitsValue", { value: fmtNum(g, 0) })} testId="actual-units-on-lot" />
            </div>
          )}

          {(hasActuals || hasGoals) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GPUChart currentGPU={currentGrossProfitPerUnit} goalGPU={goalGPU} />
              <UnitsSoldChart currentUnits={currentMonthlySales} goalUnits={goalMonthlySales} breakEvenUnits={pageBreakEvenUnits} />
            </div>
          )}

        </TabsContent>

        {/* ── Data Schedule Tab ─────────────────────────────────────────────── */}
        <TabsContent value="data" className="space-y-6">

          {/* Alert Settings — shown first */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-muted-foreground" />
                {t("pages:sla.page.alertSettings")}
              </CardTitle>
              <CardDescription>
                {t("pages:sla.page.alertSettingsDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                  {t("pages:sla.page.alertEmail")}
                </Label>
                <p className="text-xs text-muted-foreground">{t("pages:sla.page.alertEmailDesc")}</p>
                <Input
                  type="email"
                  placeholder={t("pages:sla.page.alertEmailPlaceholder")}
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  className="h-8 text-sm max-w-sm"
                  data-testid="input-sla-alert-email"
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pages:sla.page.inventoryThresholds")}</p>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between max-w-xs">
                    <Label className={`text-sm font-medium ${!alertAgingEnabled ? "text-muted-foreground" : ""}`}>{t("pages:sla.page.customAging")}</Label>
                    <Switch
                      checked={alertAgingEnabled}
                      onCheckedChange={setAlertAgingEnabled}
                      data-testid="switch-sla-alert-aging-enabled"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t("pages:sla.page.customAgingDesc")}</p>
                  <Input
                    type="number"
                    min={1}
                    placeholder={t("pages:sla.page.customAgingPlaceholder")}
                    value={alertCustomAgingDays}
                    onChange={(e) => setAlertCustomAgingDays(e.target.value)}
                    disabled={!alertAgingEnabled}
                    className={`h-8 text-sm max-w-xs ${!alertAgingEnabled ? "opacity-50" : ""}`}
                    data-testid="input-sla-alert-custom-aging"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between max-w-xs">
                    <Label className={`text-sm font-medium ${!alertHoldingCostEnabled ? "text-muted-foreground" : ""}`}>{t("pages:sla.page.maxHoldingCost")}</Label>
                    <Switch
                      checked={alertHoldingCostEnabled}
                      onCheckedChange={setAlertHoldingCostEnabled}
                      data-testid="switch-sla-alert-holding-cost-enabled"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t("pages:sla.page.maxHoldingCostDesc")}</p>
                  <div className="relative max-w-xs">
                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${!alertHoldingCostEnabled ? "text-muted-foreground/50" : "text-muted-foreground"}`}>$</span>
                    <Input
                      type="number"
                      min={0}
                      placeholder={t("pages:sla.page.maxHoldingCostPlaceholder")}
                      value={alertMaxHoldingCost}
                      onChange={(e) => setAlertMaxHoldingCost(e.target.value)}
                      disabled={!alertHoldingCostEnabled}
                      className={`pl-6 h-8 text-sm ${!alertHoldingCostEnabled ? "opacity-50" : ""}`}
                      data-testid="input-sla-alert-max-holding-cost"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between max-w-xs">
                    <Label className={`text-sm font-medium ${!alertPriceStagnationEnabled ? "text-muted-foreground" : ""}`}>{t("pages:sla.page.priceStagnation")}</Label>
                    <Switch
                      checked={alertPriceStagnationEnabled}
                      onCheckedChange={setAlertPriceStagnationEnabled}
                      data-testid="switch-sla-alert-price-stagnation-enabled"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t("pages:sla.page.priceStagnationDesc")}</p>
                  <Input
                    type="number"
                    min={1}
                    placeholder={t("pages:sla.page.priceStagnationPlaceholder")}
                    value={alertPriceStagnationDays}
                    onChange={(e) => setAlertPriceStagnationDays(e.target.value)}
                    disabled={!alertPriceStagnationEnabled}
                    className={`h-8 text-sm max-w-xs ${!alertPriceStagnationEnabled ? "opacity-50" : ""}`}
                    data-testid="input-sla-alert-price-stagnation-days"
                  />
                </div>
              </div>

              <Button
                size="sm"
                onClick={() => saveAlertSettingsMutation.mutate()}
                disabled={saveAlertSettingsMutation.isPending}
                data-testid="button-sla-save-alert-settings"
              >
                {saveAlertSettingsMutation.isPending ? t("pages:sla.common.saving") : t("pages:sla.page.saveAlertSettings")}
              </Button>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">{t("pages:sla.page.dataAccountability")}</h2>
          </div>
          <p className="text-xs text-muted-foreground -mt-3">
            {t("pages:sla.page.dataAccountabilityDesc")}
          </p>
          <DataHealthCard />
          <DataScheduleCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
